import { useQuery } from '@apollo/client';

import { USER_ACCOUNTS_QUERY } from '@monolith/queries/users';

export type OnboardingCurrentAccount = {
    id: string;
    business: string;
    accountManagers?: { id?: string | null }[] | null;
    dependents?: { id?: string | null }[] | null;
    email: string;
    name?: string | null;
    profilePhoto?: string | null;
    staffRole?: string | null;
    permissions?: {
        action: string;
        allowed?: boolean | null;
        resource: string;
    }[];
};

export const selectCurrentAccount = (
    accounts: OnboardingCurrentAccount[] | undefined,
    businessName: string,
): OnboardingCurrentAccount | undefined =>
    accounts?.find((account) => account.business === 'opus1io') ??
    accounts?.find((account) => account.business === businessName);

export const isSuperuserAccount = (
    account: OnboardingCurrentAccount | undefined,
): boolean => account?.staffRole === 'superuser';

const normalizeAclValue = (value: string): string =>
    value
        .toLowerCase()
        .replace(/^action_/, '')
        .replace(/^resource_/, '');

export const hasAccountPermission = (
    account: OnboardingCurrentAccount | undefined,
    action: string,
    resource: string,
): boolean => {
    if (isSuperuserAccount(account)) {
        return true;
    }

    const matchingRules =
        account?.permissions?.filter(
            (permission) =>
                normalizeAclValue(permission.action) ===
                    normalizeAclValue(action) &&
                normalizeAclValue(permission.resource) ===
                    normalizeAclValue(resource),
        ) ?? [];

    if (matchingRules.some((permission) => permission.allowed === false)) {
        return false;
    }
    return matchingRules.some((permission) => permission.allowed !== false);
};

export type OnboardingNavigationCapabilities = {
    canAccessAllBusinesses: boolean;
    canBookAppointment: boolean;
    canCreatePeople: boolean;
    canCreateSubscription: boolean;
    canEnrollSession: boolean;
    canGoToInvoiceReporting: boolean;
    canGoToTransactionReporting: boolean;
    canListMessages: boolean;
    canReadPeople: boolean;
    canReceiveVoice: boolean;
    canSwitchBusiness: boolean;
    canUpdateAllBusinesses: boolean;
    canUseLiveSupport: boolean;
    canViewReleaseNotes: boolean;
};

export const deriveNavigationCapabilities = (
    account: OnboardingCurrentAccount | undefined,
): OnboardingNavigationCapabilities => ({
    canAccessAllBusinesses: hasAccountPermission(
        account,
        'list',
        'all_businesses',
    ),
    canBookAppointment: hasAccountPermission(account, 'book', 'appointment'),
    canCreatePeople: hasAccountPermission(account, 'create', 'people'),
    canCreateSubscription: hasAccountPermission(
        account,
        'create',
        'subscription',
    ),
    canEnrollSession: hasAccountPermission(account, 'ACTION_ENROLL', 'session'),
    canGoToInvoiceReporting: hasAccountPermission(
        account,
        'ACTION_GOTO',
        'RESOURCE_INVOICE_REPORTING',
    ),
    canGoToTransactionReporting: hasAccountPermission(
        account,
        'ACTION_GOTO',
        'RESOURCE_TRANSACTION_REPORTING',
    ),
    canListMessages: hasAccountPermission(
        account,
        'list',
        'RESOURCE_INSTANT_MESSAGES',
    ),
    canReadPeople: hasAccountPermission(account, 'read', 'people'),
    canReceiveVoice: hasAccountPermission(
        account,
        'ACTION_RECEIVE',
        'RESOURCE_VOIP_CALL',
    ),
    canSwitchBusiness: hasAccountPermission(account, 'list', 'switch_business'),
    canUpdateAllBusinesses: hasAccountPermission(
        account,
        'update',
        'all_businesses',
    ),
    canUseLiveSupport: hasAccountPermission(
        account,
        'get',
        'RESOURCE_LIVE_HELP',
    ),
    canViewReleaseNotes: hasAccountPermission(
        account,
        'view',
        'RESOURCE_CHANGELOG',
    ),
});

export const useCurrentAccount = (email: string, businessName: string) => {
    const { data, loading, error } = useQuery(USER_ACCOUNTS_QUERY, {
        variables: { email },
        skip: !email || !businessName,
    });
    const account = selectCurrentAccount(data?.accounts, businessName);
    const capabilities = deriveNavigationCapabilities(account);

    return {
        account,
        accounts: data?.accounts ?? [],
        capabilities,
        error,
        isSuperuser: isSuperuserAccount(account),
        loading,
    };
};
